import React, {
  RefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { CORELoading } from "../Feedback/CORELoading";
import "./COREChart.less";
import { Alert, Spin } from "antd";
import Plotly, {
  Config,
  Layout,
  ModeBarButton,
  ModeBarDefaultButtons,
  PlotData,
  PlotlyHTMLElement,
} from "plotly.js/dist/plotly-myPlotly.js";
import { TestID } from "../../shared/testids/testids";
import { ConfigYAxisValue } from "../../modules/chartBuilder/AggregatesChartV2";
import { black, ColorHex, grey60, white } from "../Content/COREColour";
import classNames from "classnames";
import { PlotRefType } from "./ChartHOC";
import { PlotParams } from "react-plotly.js";
import equal from "fast-deep-equal";
import createPlotlyComponent from "react-plotly.js/factory";
import {
  applyCustomTooltipConfig,
  setupLayout,
} from "./ChartUtils/layoutConfig";
import { downloadFileName } from "./ChartUtils/csvUtils";
import { createCustomModebar } from "./ChartUtils/configUtils";
import { useTheme } from "../../theme/ThemeProvider";
import { keysToSnake } from "../../shared/global";
import {
  CORETimeSeriesChartTooltip,
  ChartPointBBox,
  COREChartTooltipState,
} from "./tooltips/CORETimeSeriesChartTooltip";

export type MarginObject = {
  l?: number;
  t?: number;
  r?: number;
  b?: number;
};

type RenderPlotlyProps = {
  scatterGL: boolean;
  plotRef?: React.MutableRefObject<PlotRefType>;
  data: COREChartProps["data"];
  layout: Partial<Layout>;
  customModebarButtons?: Array<ModeBarDefaultButtons[] | ModeBarButton[]>;
  hoverColor?: string;
  chartId: PlotParams["divId"];
  fitParent?: boolean;
  showTimeButtons?: COREChartProps["showTimeButtons"];
  margin?: COREChartProps["margin"];
  downloadPngCanvas?: COREChartProps["downloadPngCanvas"];
  useResizeHandler?: COREChartProps["useResizeHandler"];
  downloadCsv?: COREChartProps["downloadCsv"];
  downloadPng?: COREChartProps["downloadPng"];
  legendRef?: COREChartProps["legendRef"];
  titleRef?: COREChartProps["titleRef"];
  chartTitle?: COREChartProps["chartTitle"];
  loading?: COREChartProps["loading"];
  customLayout?: COREChartProps["customLayout"];
  onPlotlyHover?: COREChartProps["onPlotlyHover"];
  onPlotlyUnhover?: COREChartProps["onPlotlyUnhover"];
  onPlotlyClick?: COREChartProps["onPlotlyClick"];
  customTooltip?: COREChartProps["customTooltip"];
  interactiveTooltip?: COREChartProps["interactiveTooltip"];
};

export type ChartDataType = Pick<
  PlotData,
  | "name"
  | "x"
  | "type"
  | "line"
  | "marker"
  | "yaxis"
  | "showlegend"
  | "hoverlabel"
  | "hovertemplate"
  | "hoverinfo"
  | "textfont"
  | "ids"
  | "labels"
  | "parents"
  | "values"
  | "opacity"
  | "r"
> & {
  close?: number | null[];
  high?: number | null[];
  low?: number | null[];
  open?: number | null[];
  insidetextorientation?: "auto" | "horizontal" | "radial" | "tangential";
  leaf?: {
    opacity: number;
  };
  sort?: boolean;
  branchvalues?: "total" | "remainder";
  fill?: PlotData["fill"];
  fillcolor?: string;
  stackgroup?: PlotData["stackgroup"];
  // Not in @types/plotly.js, but both are real bar attributes.
  alignmentgroup?: string;
  offsetgroup?: string;
} & (
    | {
        mode: Exclude<PlotData["mode"], "markers">;
        y?: number[];
      }
    | {
        mode: "markers";
        y?: number[] | string[];
      }
  );

export type COREChartProps = {
  chartId: string;
  chartTitle?: string;
  enableEmptyIcon?: boolean;
  hoverColor?: ColorHex;
  config?: Partial<Config>;
  configRangeOfYAxis?: ConfigYAxisValue[];
  data: Partial<ChartDataType>[];
  layout: Partial<Layout>;
  useResizeHandler?: boolean;
  loading?: boolean;
  downloadCsv?: boolean;
  showTimeButtons?: boolean;
  downloadPng?: boolean;
  downloadPngCanvas?: boolean;
  margin?: MarginObject;
  testID: TestID;
  plotRef?: React.MutableRefObject<PlotRefType>;
  legendRef?: RefObject<HTMLDivElement>;
  titleRef?: RefObject<HTMLDivElement>;
  maxHeight?: number;
  isWidget?: boolean;
  /**
   * Sizes the chart from its container rather than from a height baked into
   * the Plotly layout. Dashboard widgets have always rendered this way, and a
   * chart expanded to full screen needs exactly the same thing — hence a name
   * that says what it does rather than which container asked for it.
   * `isWidget` still turns it on, so existing callers are unaffected.
   */
  fitParent?: RenderPlotlyProps["fitParent"];
  customLayout?: boolean;
  onPlotlyHover?: (event: Plotly.PlotMouseEvent) => void;
  onPlotlyUnhover?: () => void;
  onPlotlyClick?: (event: Plotly.PlotMouseEvent) => void;
  /** Render prop — return JSX to render a custom tooltip. Receives hover state (points + position).
   * Pass `CORETimeSeriesChartTooltip` for the default time-series tooltip, or supply your own. */
  customTooltip?: (state: COREChartTooltipState) => React.ReactNode;
  /**
   * Keeps the custom tooltip on screen for a moment after the pointer leaves the
   * mark, and hands it back to the pointer while it is hovered — needed when the
   * tooltip holds controls the user has to reach, such as a button.
   *
   * Off by default: a plain read-only tooltip should vanish the instant the
   * pointer leaves, and it also has `pointer-events: none` so it could never be
   * hovered anyway. Opting in also means the tooltip covers the marks beneath it.
   */
  interactiveTooltip?: boolean;
};

export const Plot = createPlotlyComponent(Plotly);
const scatterGLThreshold = 10000;
const removeButtons: ModeBarDefaultButtons[] = [
  "autoScale2d",
  "toImage",
  "zoom2d",
  "zoomIn2d",
  "zoomOut2d",
  "pan2d",
  "resetScale2d",
  "lasso2d",
  "select2d",
  "sendDataToCloud",
  "zoom2d",
  "pan2d",
  "zoomIn2d",
  "zoomOut2d",
  "autoScale2d",
  "resetScale2d",
  "hoverClosestCartesian",
  "hoverCompareCartesian",
  "zoom3d",
  "pan3d",
  "orbitRotation",
  "tableRotation",
  "resetCameraDefault3d",
  "resetCameraLastSave3d",
  "hoverClosest3d",
  "zoomInGeo",
  "zoomOutGeo",
  "resetGeo",
  "hoverClosestGeo",
  "hoverClosestGl2d",
  "hoverClosestPie",
  "toggleHover",
  "toImage",
  "resetViews",
  "toggleSpikelines",
];

const applyHoverColorToChart = (
  chartData: RenderPlotlyProps["data"],
  pointIndex: number,
  hoverColor: RenderPlotlyProps["hoverColor"]
) => {
  return chartData.map((x) =>
    x.type === "bar"
      ? {
          ...x,
          marker: {
            color: Array.isArray(x?.marker?.color)
              ? (x?.marker?.color as Plotly.Color[]).map(
                  (d: Plotly.Color, i: number) =>
                    i === pointIndex ? hoverColor : d
                )
              : [],
          },
        }
      : x
  ) as RenderPlotlyProps["data"];
};

const mapData = (ds: COREChartProps["data"]) => {
  let scatterGL: boolean = false;
  // if not selected trace, It should be false
  const data: Partial<ChartDataType>[] = ds.map((datalist) => {
    const { type, x, ...other } = datalist;
    const checkScatter = x
      ? type === "scatter" && x.length >= scatterGLThreshold
      : false;
    scatterGL = checkScatter;
    return checkScatter ? { ...other, type: "scattergl", x } : datalist;
  });

  return {
    data,
    scatterGL,
  };
};

const MemoizedPlot = React.memo<
  React.ComponentType<PlotParams & { ref?: React.ForwardedRef<any> }>
>(
  Plot,
  (prevProps, nextProps) =>
    prevProps &&
    equal(prevProps.data, nextProps.data) &&
    equal(prevProps.layout, nextProps.layout)
);

const getCSSVariable = (variableName: string) => {
  if (typeof window === "undefined") return undefined;
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
};

const getCSSVariableWithFallback = (variableName: string, fallback: string) => {
  const value = getCSSVariable(variableName);
  return value || fallback; // fallback if undefined or empty string
};

/**
 * Converts the bounding box plotly reports for a hovered point into viewport
 * coordinates, so a custom tooltip can anchor itself to the mark.
 *
 * Plotly builds `point.bbox` as `pixelWithinPlot + gd.offsetLeft/Top +
 * gd.clientLeft/Top`, i.e. relative to the graph div's offset parent. Undoing
 * the offset and adding the graph div's own viewport rect lands on the mark.
 * `bbox` is undocumented in `@types/plotly.js`, hence the cast.
 */
const toViewportBBox = (
  gd: PlotlyHTMLElement | undefined,
  point: Plotly.PlotDatum
): ChartPointBBox | undefined => {
  const bbox = (point as unknown as { bbox?: ChartPointBBox }).bbox;
  if (!gd || !bbox) return undefined;

  const rect = gd.getBoundingClientRect();
  return {
    x0: bbox.x0 - gd.offsetLeft + rect.left,
    x1: bbox.x1 - gd.offsetLeft + rect.left,
    y0: bbox.y0 - gd.offsetTop + rect.top,
    y1: bbox.y1 - gd.offsetTop + rect.top,
  };
};

/**
 * How long an interactive tooltip survives `plotly_unhover`.
 *
 * Long enough for the pointer to cross the gap between the mark and the
 * tooltip, short enough that a tooltip left behind on the way out of the chart
 * does not feel stuck.
 */
const INTERACTIVE_TOOLTIP_HIDE_DELAY_MS = 200;

/**
 * Gives an interactive tooltip the wrapper that cancels its pending hide once
 * the pointer lands on it. The tooltip is positioned `fixed`, so the wrapper
 * has no box of its own, but enter and leave still reach it through the DOM
 * tree. Doing it here means a consumer only has to drop `pointer-events: none`
 * from its tooltip to make it reachable.
 *
 * A read-only tooltip is returned untouched, so opting out leaves the rendered
 * markup exactly as it was.
 */
const renderTooltipWithHoverGuard = (
  tooltip: React.ReactNode,
  interactiveTooltip: boolean,
  onPointerArrive: () => void,
  onPointerLeave: () => void
): React.ReactNode =>
  interactiveTooltip ? (
    <div onMouseEnter={onPointerArrive} onMouseLeave={onPointerLeave}>
      {tooltip}
    </div>
  ) : (
    tooltip
  );

export const RenderPlotly = ({
  fitParent = false,
  layout,
  data,
  hoverColor,
  onPlotlyHover,
  onPlotlyUnhover,
  onPlotlyClick,
  customTooltip,
  interactiveTooltip = false,
  ...props
}: RenderPlotlyProps) => {
  const [hoverPointIndex, setHoverPointIndex] = useState<number | null>();
  const [tooltipState, setTooltipState] =
    useState<COREChartTooltipState | null>(null);

  // Pending hide from the last unhover, cancelled when the pointer arrives on
  // the tooltip or lands on another mark.
  const hideTooltipTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelHideTooltip = useCallback(() => {
    clearTimeout(hideTooltipTimer.current);
  }, []);

  const hideTooltip = useCallback(() => {
    cancelHideTooltip();
    setTooltipState(null);
  }, [cancelHideTooltip]);

  useEffect(() => cancelHideTooltip, [cancelHideTooltip]);

  const chartData = useMemo(() => {
    let d = data;
    if (typeof hoverPointIndex === "number") {
      d = applyHoverColorToChart(d, hoverPointIndex, hoverColor);
    }
    if (customTooltip) {
      d = applyCustomTooltipConfig(d);
    }
    return d;
  }, [data, hoverPointIndex, hoverColor, customTooltip]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const node = props.plotRef?.current?.el;
      if (node && node instanceof HTMLElement) {
        try {
          Plotly.Plots.resize(node);
        } catch (e) {
          console.warn("Plotly resize failed", e);
        }
      }
    }, 300); // Delay to ensure layout is complete

    return () => clearTimeout(timeout);
  }, [props.plotRef]);

  const onChartHover = useCallback(
    (eventdata: Readonly<Plotly.PlotMouseEvent>) => {
      // Internal logic for hoverColor feature
      if (hoverColor) {
        setHoverPointIndex(eventdata.points[0].pointIndex);
      }

      // Internal logic for custom tooltip feature
      if (customTooltip && eventdata.points.length > 0) {
        // Another mark won the pointer, so a hide queued by the mark we just
        // left would wipe out the tooltip we are about to show.
        cancelHideTooltip();
        setTooltipState({
          points: eventdata.points,
          position: {
            x: (eventdata.event as MouseEvent).clientX,
            y: (eventdata.event as MouseEvent).clientY,
          },
          pointBBox: toViewportBBox(
            props.plotRef?.current?.el as PlotlyHTMLElement | undefined,
            eventdata.points[0]
          ),
        });
      }

      // External callback for custom hover handling
      onPlotlyHover?.(eventdata);
    },
    [hoverColor, onPlotlyHover, customTooltip, props.plotRef, cancelHideTooltip]
  );

  const onChartUnHover = useCallback(() => {
    // Internal logic for hoverColor feature
    if (hoverColor) {
      setHoverPointIndex(null);
    }

    // Internal logic for custom tooltip feature
    if (customTooltip) {
      // Leaving the mark for the tooltip itself fires this first and the
      // tooltip's own mouseenter second, so an interactive tooltip has to
      // outlive the unhover long enough for that second event to cancel it.
      if (interactiveTooltip) {
        hideTooltipTimer.current = setTimeout(
          hideTooltip,
          INTERACTIVE_TOOLTIP_HIDE_DELAY_MS
        );
      } else {
        setTooltipState(null);
      }
    }

    // External callback for custom unhover handling
    onPlotlyUnhover?.();
  }, [
    hoverColor,
    onPlotlyUnhover,
    customTooltip,
    interactiveTooltip,
    hideTooltip,
  ]);

  const onChartClick = useCallback(
    (eventdata: Readonly<Plotly.PlotMouseEvent>) => {
      onPlotlyClick?.(eventdata);
    },
    [onPlotlyClick]
  );

  const bindHoverListeners = useCallback(
    (node?: PlotlyHTMLElement) => {
      if (!node?.removeAllListeners || !node?.on) {
        return;
      }

      node.removeAllListeners("plotly_hover");
      node.removeAllListeners("plotly_unhover");
      node.removeAllListeners("plotly_click");
      node.on("plotly_hover", onChartHover);
      node.on("plotly_unhover", onChartUnHover);
      node.on("plotly_click", onChartClick);
    },
    [onChartHover, onChartUnHover, onChartClick]
  );

  useEffect(() => {
    bindHoverListeners(props.plotRef?.current?.el as PlotlyHTMLElement);
  }, [bindHoverListeners, props.plotRef]);

  if (fitParent) {
    layout.autosize = true;
    delete layout.height;
  }

  // We call useTheme() only to subscribe to theme changes.
  // This ensures the chart re-renders and picks up updated CSS variables.
  // The 'theme' value is not directly used here.
  const { theme } = useTheme();

  const mergedLayout = useMemo(() => {
    const chartBgColor = getCSSVariableWithFallback(
      "--core-chart-bgcolor",
      white
    );
    const chartLabelColor = getCSSVariableWithFallback("--body-text", black);
    const chartBorderColor = getCSSVariableWithFallback(
      "--core-chart-border-color",
      grey60
    );

    return {
      ...layout,
      ...keysToSnake({
        paperBgcolor: chartBgColor,
        plotBgcolor: chartBgColor,
      }),
      font: {
        ...(layout.font ?? {}),
        color: chartLabelColor,
      },
      xaxis: {
        ...(layout.xaxis ?? {}),
        linecolor: chartBorderColor,
        gridcolor: layout.xaxis?.gridcolor ?? chartBorderColor,
      },
      yaxis: {
        ...(layout.yaxis ?? {}),
        linecolor: chartBorderColor,
        gridcolor: layout.yaxis?.gridcolor ?? chartBorderColor,
      },
      yaxis2: {
        ...(layout.yaxis2 ?? {}),
        linecolor: chartBorderColor,
        gridcolor: layout.yaxis2?.gridcolor ?? chartBorderColor,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, theme]);

  const chartStyle = fitParent
    ? { width: "100%", height: "100%", minHeight: "300px" }
    : layout.width
    ? {}
    : { width: "100%" };

  return (
    <Suspense fallback={<CORELoading message={"Loading Chart"} size={"lg"} />}>
      {tooltipState &&
        ReactDOM.createPortal(
          customTooltip ? (
            renderTooltipWithHoverGuard(
              customTooltip(tooltipState),
              interactiveTooltip,
              cancelHideTooltip,
              hideTooltip
            )
          ) : (
            <CORETimeSeriesChartTooltip {...tooltipState} />
          ),
          document.body
        )}
      {props.scatterGL ? (
        <Alert
          type="info"
          message="Due to the large number of points, we are displaying this chart in a way that can be blurry. To see sections of the chart in full detail, reduce the date range or increase the aggregation period."
          closable
        />
      ) : null}
      <MemoizedPlot
        divId={props.chartId}
        ref={props.plotRef}
        data={chartData}
        layout={mergedLayout}
        useResizeHandler
        config={{
          displaylogo: false,
          modeBarButtons: props.customModebarButtons,
          modeBarButtonsToRemove: removeButtons,
          displayModeBar: true,
          toImageButtonOptions: {
            width: 1000,
            format: "png",
            filename: downloadFileName(`${layout.filename ?? "Newplot"}`),
            scale: 1,
          },
        }}
        onInitialized={(_, el) => {
          bindHoverListeners(el as PlotlyHTMLElement);
        }}
        style={chartStyle}
      />
    </Suspense>
  );
};

export const COREChart: React.FC<COREChartProps> = ({
  maxHeight,
  isWidget,
  fitParent: fitParentProp,
  plotRef,
  data,
  showTimeButtons = true,
  margin,
  downloadPngCanvas,
  useResizeHandler,
  layout,
  downloadCsv,
  downloadPng,
  legendRef,
  titleRef,
  chartTitle,
  loading = false,
  customLayout,
  onPlotlyHover,
  onPlotlyUnhover,
  onPlotlyClick,
  ...props
}) => {
  // A widget is simply the container that has always needed this mode; full
  // screen is the second one.
  const fitParent = fitParentProp || isWidget;

  useEffect(() => {
    const resizeCurrent = plotRef?.current?.resizeHandler;
    if (resizeCurrent) resizeCurrent();
  }, [plotRef]);

  const { data: mappedData, scatterGL } = mapData(data);

  const customModeBarButtons: Array<ModeBarDefaultButtons[] | ModeBarButton[]> =
    createCustomModebar(
      mappedData,
      downloadCsv,
      downloadPng,
      downloadPngCanvas,
      plotRef,
      legendRef,
      titleRef,
      chartTitle
    );

  const renderPlotlyProps: RenderPlotlyProps = {
    ...props,
    plotRef,
    showTimeButtons,
    margin,
    downloadPngCanvas,
    useResizeHandler,
    downloadCsv,
    downloadPng,
    legendRef,
    titleRef,
    chartTitle,
    loading,
    customLayout,
    onPlotlyHover,
    onPlotlyUnhover,
    onPlotlyClick,
    data: mappedData,
    layout: setupLayout(
      layout,
      fitParent,
      false,
      true,
      useResizeHandler,
      downloadPngCanvas,
      margin,
      customLayout,
      showTimeButtons
    ),
    scatterGL,
    ...(mappedData.length !== 0 ? { customModeBarButtons } : {}),
  };

  if (fitParent) {
    return (
      <div
        className={classNames(
          "core-chart-fit-parent",
          "dashboard-widget-chart"
        )}
      >
        <Spin spinning={loading} delay={100}>
          <div
            style={{ height: maxHeight ?? "100%" }}
            className={classNames("core-chart-block")}
          >
            <RenderPlotly fitParent {...renderPlotlyProps} />
          </div>
        </Spin>
      </div>
    );
  }

  return (
    <Spin spinning={loading} delay={100}>
      <div className={classNames("core-chart-block")}>
        <RenderPlotly {...renderPlotlyProps} />
      </div>
    </Spin>
  );
};
